import React, { useState, useMemo, useEffect } from 'react'
import { CircularProgress } from '@mui/material'
import {
  formatBytes,
  calculateDataRowCountFromFileStore,
  createFileStore,
  isFileDeleted,
  buildKeyFieldChangeUpdates,
  buildDataCategoryChangeUpdates,
  FILE_STORE_KEYS
} from '../extra/helpers'
import { FILE_STATE, FILE_TYPE } from '../extra/types'
import { DATA_CATEGORY, DATA_CATEGORY_OPTIONS, PERIOD_KIND_MONTHLY } from '../extra/constants'
import { formatCommaDigit } from '../../../../common/utils/NumberUtils'
import { formatDate } from '../../../../common/utils/DateUtils'
import CustomTooltip from '../../../../_components/CustomTooltip'
import { ChrofyCustomSelect } from '../../../common/ChrofyCustomSelect'
import DetailSettings from './DetailSettings'
import '../../../../assets/css/style.css'
import '../../../../assets/css/common.css'
import '../extra/types.js'

/**
 * @typedef {Object} FileCardProps
 * @property {import('../extra/types').FileState} file
 * @property {string} fileKey
 * @property {string} taskKey
 * @property {import('../extra/types').FileClassificationListStructure} fileClassificationList
 * @property {Set<string> | undefined} [fileClassificationsInUseByOthers] 同一タスク内で他ファイルが使用中の種別（select から除外する）
 * @property {import('../extra/types').IDConversionTable[]} idConversionTables
 * @property {boolean} isExpanded
 * @property {function(string): void} onToggleExpand
 * @property {function(string, any): void} onUpdateFile
 * @property {function(string): void} onDeleteFile
 * @property {function(string): void} onChangeFile
 * @property {function(string): void} onShowFileContent
 * @property {function(string, string): void} onChangeSheet
 * @property {function(string, string, number, string): Promise<Set<string>>} onGetUniqueValues
 * @property {function(string, string): void} onChangeFileClassification ファイル種別変更（IF02104 の過去設定自動反映を含む）
 * @property {string} [periodKind] タスクの期間種別（'monthly' の場合のみベースファイルトグルを表示）
 * @property {function(string, any): void} onSyncFileMeta
 * @property {boolean} [isTaskFixed]
 * @property {string} [savedFileClassification] 最終登録ベースラインのファイル種別（savedUploadState）。未保存の選択のみのときはロックしない
 * @property {string} [savedPrimaryKeyColumn] 最終登録ベースラインのキー項目（savedUploadState）
 * @property {string} [savedDataCategory] 最終登録ベースラインのデータ種別（savedUploadState）
 * @property {boolean} [isDetailSettingsLockedByMapping] マッピング設定で当該ファイル種別が使用中
 * @property {number[]} [exclusionValueErrorIndices] 登録時バリデーションで除外値未選択となった条件インデックス
 */

/**
 * FileCardコンポーネント
 *
 * @param {FileCardProps} props
 */
const FileCard = ({
  file,
  fileKey,
  taskKey,
  fileClassificationList,
  fileClassificationsInUseByOthers,
  idConversionTables,
  isExpanded,
  onToggleExpand,
  onUpdateFile,
  onSyncFileMeta,
  onDeleteFile,
  onChangeFile,
  onShowFileContent,
  onChangeSheet,
  onGetUniqueValues,
  onChangeFileClassification,
  periodKind = '',
  isTaskFixed = false,
  savedFileClassification = '',
  savedPrimaryKeyColumn = '',
  savedDataCategory = '',
  isDetailSettingsLockedByMapping = false,
  exclusionValueErrorIndices = []
}) => {
  const [searchKeyword, setSearchKeyword] = useState({})

  const isDeleted = isFileDeleted(file.fileState)
  const isVirusCheckInProgress = file.fileState === FILE_STATE.VIRUS_CHECK_IN_PROGRESS
  const isVirusChecking =
    file.fileState === FILE_STATE.VIRUS_CHECK_IN_PROGRESS || file.fileState === FILE_STATE.VIRUS_CHECK_ERROR
  const hasFileType = !!file.fileClassification
  const hasSavedFileClassification = Boolean(String(savedFileClassification ?? '').trim())
  const hasSavedPrimaryKey = Boolean(String(savedPrimaryKeyColumn ?? '').trim())
  const hasSavedDataCategory = Boolean(String(savedDataCategory ?? '').trim())
  // 仕様（画面状態②）: ファイル種別選択時はキー項目・データ種別とも必須
  const isKeyFieldRequired = hasFileType
  const headers = Array.isArray(file.headers) ? file.headers : []
  const isKeyFieldDisabled =
    isDeleted ||
    headers.length === 0 ||
    !hasFileType ||
    isVirusChecking ||
    isTaskFixed ||
    (hasSavedFileClassification && hasSavedPrimaryKey)
  // データ種別の活性条件はキー項目と同様（選択肢は headers に依存しないため headers 空チェックは行わない）
  const isDataCategoryDisabled =
    isDeleted || !hasFileType || isVirusChecking || isTaskFixed || (hasSavedFileClassification && hasSavedDataCategory)
  const isFileTypeDisabled = isVirusChecking || isTaskFixed || hasSavedFileClassification
  // 仕様（画面状態⑨）: 月次タスクの場合のみベースファイルトグルを表示する
  const isMonthlyTask = periodKind === PERIOD_KIND_MONTHLY
  // 登録済み（uploaded）でも切替可能。変更して登録すると IF02080 が除外条件変更時と
  // 同様に GMR 再構築とデータ分類ステータスの巻き戻しを行う。
  // 仕様（画面状態⑨）: データ種別が「従業員別」のときのみ活性
  const isBaseFileToggleDisabled =
    isDeleted || isVirusChecking || isTaskFixed || file.dataCategory !== DATA_CATEGORY.EMPLOYEE

  // データ取得中スピナー：初期化時のバックグラウンドダウンロードが実行中のときだけ表示する。
  // ダウンロードが失敗・未実行のファイル（元ファイル削除済みの検証エラー／予期せぬエラー等）では
  // isStoredLocally が false のままでもスピナーを出さない（無限ローディング防止）。
  const isDataLoading = !isDeleted && file.isDownloading === true

  const isExcel = file.fileType === FILE_TYPE.EXCEL

  // メタ同期: ローカルストアにheadersがあるのにstoreに無い場合の救済
  useEffect(() => {
    const hydrateHeadersFromLocal = async () => {
      if (isDeleted) return
      if (isVirusCheckInProgress) return
      if (headers.length > 0) return
      if (!file.isStoredLocally) return
      if (typeof onSyncFileMeta !== 'function') return

      try {
        const fileStore = createFileStore(fileKey, taskKey)

        const storedHeaders = await fileStore.getItem(FILE_STORE_KEYS.HEADERS)
        if (Array.isArray(storedHeaders) && storedHeaders.length > 0) {
          onSyncFileMeta(fileKey, { headers: storedHeaders })
          return
        }

        const reference = await fileStore.getItem(FILE_STORE_KEYS.REFERENCE)
        const refHeaders = reference?.headers
        if (Array.isArray(refHeaders) && refHeaders.length > 0) {
          onSyncFileMeta(fileKey, { headers: refHeaders })
        }
      } catch (_e) {
        // silent
      }
    }

    hydrateHeadersFromLocal()
  }, [file.isStoredLocally, fileKey, headers.length, isDeleted, isVirusCheckInProgress, onSyncFileMeta])

  // ファイル種別の選択肢を生成（デフォルト + ユーザー追加を結合。削除予定は除外。同一タスク内で他ファイルが使用中は除外、自ファイルの現在値は残す）
  // 登録済みでも savedUploadState 上の種別が未設定なら選べ続ける（ロックは最終登録ベースラインに種別があるときのみ）
  const fileTypeOptions = useMemo(() => {
    const userTypes = (fileClassificationList.userTypes || []).filter((ft) => !ft.isPendingDelete)
    const allTypes = [...(fileClassificationList.defaultTypes || []), ...userTypes]
    const options = allTypes.map((ft) => ({
      value: ft.fileClassification, // ラベル文字列を値として使用
      label: ft.fileClassification
    }))
    if (!fileClassificationsInUseByOthers) return options
    return options.filter(
      (opt) => !fileClassificationsInUseByOthers.has(opt.value) || opt.value === file.fileClassification
    )
  }, [fileClassificationList, file.fileClassification, fileClassificationsInUseByOthers])

  // キー項目の選択肢を生成
  const keyFieldOptions = useMemo(() => {
    return headers.map((header) => ({
      value: header,
      label: header
    }))
  }, [headers])

  const handleFileTypeChange = (e) => {
    const newFileType = e.target.value // ラベル文字列
    // 仕様 9.9: 種別選択時は IF02104 の過去設定自動反映を含むハンドラへ委譲する
    onChangeFileClassification(fileKey, newFileType)
  }

  /** キー項目変更。未選択に戻した場合は従業員ID変換表をクリアし、選択列がハッシュ化対象列にある場合はチェックを外す */
  const handleKeyFieldChange = (e) => {
    onUpdateFile(fileKey, buildKeyFieldChangeUpdates(e.target.value, file.hashColumns))
  }

  /** データ種別変更。「従業員別」以外へ変更した場合はベースファイルフラグ・従業員ID変換表をクリアする（仕様 画面状態⑨） */
  const handleDataCategoryChange = (e) => {
    onUpdateFile(fileKey, buildDataCategoryChangeUpdates(e.target.value))
  }

  const handleSheetChange = (e) => {
    const newSheetName = e.target.value
    onChangeSheet(fileKey, newSheetName)
  }

  const dateTimeSource = file.updatedAt || file.uploadedDateTime
  const dateForDisplay = dateTimeSource ? new Date(dateTimeSource) : null
  const uploadTime =
    dateForDisplay && !Number.isNaN(dateForDisplay.getTime()) ? formatDate(dateForDisplay, 'MM/dd HH:mm') : '-'
  const fileSize = formatBytes(file.fileSize)
  const [recordCount, setRecordCount] = useState(0)

  // 行数を計算（ヘッダー行を除く。レポートデータ更新画面の表示に合わせる）
  useEffect(() => {
    const calculateRowCount = async () => {
      if (file.isStoredLocally && !isDeleted) {
        try {
          const fileStore = createFileStore(fileKey, taskKey)
          const count = await calculateDataRowCountFromFileStore(fileStore)
          setRecordCount(count)
        } catch (error) {
          console.error('Failed to calculate row count:', error)
          setRecordCount(0)
        }
      } else {
        // IndexedDBに保存されていない場合、または削除済みの場合は0
        setRecordCount(0)
      }
    }
    calculateRowCount()
  }, [fileKey, file.isStoredLocally, isDeleted, taskKey])

  // 行数表示: ローカル計算値が無い場合はサーバ提供の行数（IF02101 row_count = file.rowCount）にフォールバックする。
  // レポートデータ更新画面と同じ値を表示し、エラーでローカル未保持のファイルでも行数を出す。
  const displayRowCount = recordCount > 0 ? recordCount : Number(file.rowCount) > 0 ? Number(file.rowCount) : null

  return (
    <div
      className={`bg-white border-2 rounded-lg mb-3 shadow-sm transition-all ${
        isDeleted ? 'bg-gray-300/60' : 'border-[#cbd5e1] hover:border-[#3150a5] hover:shadow-md'
      }`}
    >
      <div className="flex items-center justify-between px-4 py-3 bg-[#f1f5f9] border-b border-[#cbd5e1] rounded-t-lg">
        <div className="flex items-center gap-4 flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="text-lg">📄</span>
            <CustomTooltip title={file.fileName}>
              {(ref) => (
                <span
                  ref={ref}
                  className={`text-body font-semibold text-[#1e293b] truncate max-w-[400px] ${isDeleted ? 'line-through' : ''}`}
                >
                  {file.fileName}
                </span>
              )}
            </CustomTooltip>
          </div>
          <div className="flex items-center gap-3 text-xs text-[#64748b] flex-wrap flex-shrink">
            <span>📊 {displayRowCount != null ? formatCommaDigit(String(displayRowCount)) : '計算中'}行</span>
            <span>📋 {headers.length}列</span>
            <span>💾 {fileSize}</span>
            <span>🕐 {uploadTime}</span>
            <span>👤 {file.uploadedUsername || 'ユーザー'}</span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {isExcel &&
            (!file.isUploadedLocally || file.fileState === FILE_STATE.VIRUS_CHECK_IN_PROGRESS ? (
              // S3からダウンロード済みのExcel: シート名を表示のみ（非活性）。長いシート名は省略表示し、全体はツールチップで確認できる
              <CustomTooltip title={file.sheetName || 'シート1'}>
                {(ref) => (
                  <div
                    ref={ref}
                    className="px-3 py-1.5 border border-blue2-lighten-40 rounded-md text-sm bg-blue2-lighten-50 opacity-60 w-[150px] truncate text-blue2-darken-20"
                  >
                    {file.sheetName || 'シート1'}
                  </div>
                )}
              </CustomTooltip>
            ) : (
              // ローカルアップロードのみのExcel: シート選択可能
              file.sheetList &&
              file.sheetList.length > 0 && (
                <ChrofyCustomSelect
                  value={file.sheetName}
                  onChange={(e) => onChangeSheet(fileKey, e.target.value)}
                  options={file.sheetList.map((name) => ({ label: name, value: name }))}
                  className="w-[150px]"
                  selectClassName="!py-1.5 !rounded-md !bg-white"
                  disabled={isDeleted || isVirusCheckInProgress}
                  hasNoSelect={false}
                  dropdownPlacement="inline"
                />
              )
            ))}
          <button
            type="button"
            className="btn !bg-[#28a745] hover:!bg-[#218838] !text-white border-0 rounded text-sm !py-1.5 px-3 whitespace-nowrap disabled:opacity-50 flex items-center gap-2"
            onClick={() => onShowFileContent(fileKey)}
            disabled={isDeleted || file.fileState === FILE_STATE.VIRUS_CHECK_IN_PROGRESS || !file.isStoredLocally}
          >
            {isDataLoading && <CircularProgress size={16} color="inherit" />}
            データ表示
          </button>
          <button
            type="button"
            className="btn !bg-[#E0E5F2] !text-[#3150A5] hover:!bg-[#d0d5e2] border-0 rounded text-sm !py-1.5 px-3 whitespace-nowrap disabled:opacity-50"
            onClick={() => onChangeFile(fileKey)}
            disabled={isDeleted || isVirusCheckInProgress || isTaskFixed}
          >
            ファイル変更
          </button>
          <button
            type="button"
            className="btn red white--text rounded text-sm !py-1.5 px-3 whitespace-nowrap disabled:opacity-50"
            onClick={() => onDeleteFile(fileKey)}
            disabled={isDeleted || isVirusCheckInProgress || isTaskFixed}
          >
            削除
          </button>
        </div>
      </div>

      <div className="p-4">
        <div className="flex gap-4 mb-4 flex-wrap">
          <div className="flex-[2] min-w-[220px]">
            <label className="text-sm text-gray-600 font-medium mb-1 block flex items-center gap-1">
              ファイル種別
              <CustomTooltip
                forceShow={true}
                title={
                  <div className="p-1">
                    このファイルが何のデータかを識別するための名前です。
                    <br />
                    例：「給与データ」「勤怠データ」など
                    <br />
                    <br />
                    月ごとにファイル名が変わっても、同じ種別を設定することでマッピング設定を再利用できます。
                  </div>
                }
              >
                {(ref) => (
                  <span
                    ref={ref}
                    className="w-4 h-4 rounded-full bg-gray-400 text-white text-[11px] font-bold flex items-center justify-center cursor-help"
                  >
                    ?
                  </span>
                )}
              </CustomTooltip>
            </label>
            <ChrofyCustomSelect
              className="w-full"
              selectClassName={`!py-2 !rounded-md transition-all ${
                hasFileType
                  ? 'border-green-500 bg-green-50 has-value'
                  : 'border-amber-500 bg-amber-50 required focus:border-blue2 focus:ring-2 focus:ring-blue2/15'
              }`}
              options={fileTypeOptions}
              value={file.fileClassification || ''}
              onChange={handleFileTypeChange}
              disabled={isFileTypeDisabled}
              placeholder="-- 選択してください --"
              dropdownPlacement="inline"
            />
          </div>
          <div className="flex-[2] min-w-[220px]">
            <label className="text-sm text-gray-600 font-medium mb-1 block flex items-center gap-1">
              キー項目
              <CustomTooltip
                forceShow={true}
                title={
                  <div className="p-1">
                    従業員を一意に特定できる項目（従業員番号、社員IDなど）を選択してください。
                    <br />
                    <br />
                    複数ファイルを結合する際に、この項目を使って同じ従業員のデータを紐づけます。
                  </div>
                }
              >
                {(ref) => (
                  <span
                    ref={ref}
                    className="w-4 h-4 rounded-full bg-gray-400 text-white text-[11px] font-bold flex items-center justify-center cursor-help"
                  >
                    ?
                  </span>
                )}
              </CustomTooltip>
            </label>
            <ChrofyCustomSelect
              className="w-full"
              selectClassName={`!py-2 !rounded-md transition-all ${
                file.primaryKeyColumn
                  ? 'border-green-500 bg-green-50 has-value'
                  : isKeyFieldRequired
                    ? 'border-amber-500 bg-amber-50 required focus:border-blue2 focus:ring-2 focus:ring-blue2/15'
                    : 'border-gray-300 focus:border-blue2 focus:ring-2 focus:ring-blue2/15'
              } ${isKeyFieldDisabled ? 'bg-gray-100 opacity-60 cursor-not-allowed' : 'bg-white'}`}
              options={keyFieldOptions}
              value={file.primaryKeyColumn || ''}
              onChange={handleKeyFieldChange}
              disabled={isKeyFieldDisabled}
              placeholder="-- 選択してください --"
              dropdownPlacement="inline"
            />
          </div>
          <div className="flex-[2] min-w-[220px]">
            <label className="text-sm text-gray-600 font-medium mb-1 block">データ種別</label>
            <ChrofyCustomSelect
              className="w-full"
              selectClassName={`!py-2 !rounded-md transition-all ${
                file.dataCategory
                  ? 'border-green-500 bg-green-50 has-value'
                  : isKeyFieldRequired
                    ? 'border-amber-500 bg-amber-50 required focus:border-blue2 focus:ring-2 focus:ring-blue2/15'
                    : 'border-gray-300 focus:border-blue2 focus:ring-2 focus:ring-blue2/15'
              } ${isDataCategoryDisabled ? 'bg-gray-100 opacity-60 cursor-not-allowed' : 'bg-white'}`}
              options={[...DATA_CATEGORY_OPTIONS]}
              value={file.dataCategory || ''}
              onChange={handleDataCategoryChange}
              disabled={isDataCategoryDisabled}
              placeholder="-- 選択してください --"
              dropdownPlacement="inline"
            />
          </div>
          {isMonthlyTask && (
            <div className="flex-[1] min-w-[150px] flex flex-col items-end">
              <label className="text-sm text-gray-600 font-medium mb-1 block">ベースファイル</label>
              <div className="flex items-center h-[40px]">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={file.isBaseFile || false}
                    onChange={(e) => onUpdateFile(fileKey, { isBaseFile: e.target.checked })}
                    disabled={isBaseFileToggleDisabled}
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>
            </div>
          )}
        </div>

        <div
          className={`flex items-center gap-2 py-2 mt-3 border-t border-dashed border-gray-300 cursor-pointer transition-colors hover:text-[#3150a5] ${
            isExpanded ? '' : ''
          }`}
          onClick={() => onToggleExpand(fileKey)}
        >
          <span className={`transition-transform ${isExpanded ? 'rotate-90' : ''}`}>▶</span>
          <span className="text-body">詳細設定</span>
          <span className="text-caption-2 text-[#94a3b8]">（ID変換表 / 除外条件）</span>
        </div>

        {isExpanded && (
          <DetailSettings
            file={file}
            fileKey={fileKey}
            idConversionTables={idConversionTables}
            searchKeyword={searchKeyword}
            onSearchKeywordChange={setSearchKeyword}
            onUpdateFile={onUpdateFile}
            onGetUniqueValues={onGetUniqueValues}
            readOnly={isVirusCheckInProgress || isTaskFixed}
            isDetailSettingsLockedByMapping={isDetailSettingsLockedByMapping}
            exclusionValueErrorIndices={exclusionValueErrorIndices}
          />
        )}
      </div>
    </div>
  )
}

export default FileCard
